'use client';

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  InputGroup,
  InputLeftElement,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Stack,
  Select,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import xhr from 'xhr';
import { Search2Icon, EditIcon, DeleteIcon } from '@chakra-ui/icons';

export default function StudentSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const cancelRef = useRef();
  const [studentToDelete, setStudentToDelete] = useState(null);
  const timeoutRef = useRef(null);
  const toast = useToast();

  const searchStudents = async (document) => {
    if (!document) return;

    try {
      setLoading(true);
      setError('');
      const response = await xhr.get(`/student-graduateds?filters[document][$eq]=${document}`);
      setStudents(response.data.data);
    } catch (err) {
      setError('Error al buscar estudiantes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchTerm.trim() === '') {
      setStudents([]);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      searchStudents(searchTerm);
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEdit = (student) => {
    setSelectedStudent({ ...student });
    onOpen();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedStudent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedStudent) return;

    try {
      setIsSubmitting(true);
      // Actualizar el estudiante en el servidor
      await xhr.put(`/student-graduateds/${selectedStudent.id}`, {
        data: selectedStudent,
      });

      // Actualizar la lista de estudiantes
      const updatedStudents = students.map((student) =>
        student.id === selectedStudent.id ? selectedStudent : student
      );
      setStudents(updatedStudents);

      toast({
        title: 'Estudiante actualizado',
        description: 'Los datos del estudiante han sido actualizados correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estudiante',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    onAlertOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!studentToDelete) return;

    try {
      // Eliminar el estudiante del servidor
      await xhr.delete(`/student-graduateds/${studentToDelete.id}`);

      // Actualizar la lista de estudiantes
      const updatedStudents = students.filter((student) => student.id !== studentToDelete.id);
      setStudents(updatedStudents);

      toast({
        title: 'Estudiante eliminado',
        description: 'El estudiante ha sido eliminado correctamente',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onAlertClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el estudiante',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    }
  };

  return (
    <Box p={4}>
      <Box mb={6}>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Buscar Estudiante
        </Text>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <Search2Icon size={20} />
          </InputLeftElement>
          <Input placeholder="Buscar por documento" value={searchTerm} onChange={handleSearch} maxWidth="400px" />
        </InputGroup>
      </Box>

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={8}>
          <Spinner />
        </Box>
      ) : students.length > 0 ? (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Nombre</Th>
                <Th>Documento</Th>
                <Th>Tipo Doc.</Th>
                <Th>Institución</Th>
                <Th>Programa</Th>
                <Th>Estado</Th>
                <Th>Fecha límite de pago</Th>
                <Th>Valor</Th>
                <Th>Semestre</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {students.map((student) => (
                <Tr key={student.id}>
                  <Td>{student.name}</Td>
                  <Td>{student.document}</Td>
                  <Td>{student.type_document}</Td>
                  <Td>{student.institution}</Td>
                  <Td>{student.program_finished}</Td>
                  <Td>{student.status}</Td>
                  <Td>{student.datePayment}</Td>
                  <Td>{student.valor}</Td>
                  <Td>{student.semester}</Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<EditIcon />}
                      onClick={() => handleEdit(student)}
                      mr={2}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="red"
                      leftIcon={<DeleteIcon />}
                      onClick={() => handleDeleteClick(student)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : searchTerm ? (
        <Box textAlign="center" my={8}>
          <Text>No se encontraron estudiantes</Text>
        </Box>
      ) : null}

      {/* Modal de edición */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Estudiante</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedStudent && (
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel>Nombre</FormLabel>
                  <Input name="name" value={selectedStudent.name || ''} onChange={handleInputChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Documento</FormLabel>
                  <Input name="document" value={selectedStudent.document || ''} onChange={handleInputChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Tipo de Documento</FormLabel>
                  <Select name="type_document" value={selectedStudent.type_document || ''} onChange={handleInputChange}>
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="PP">Pasaporte</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Institución</FormLabel>
                  <Input name="institution" value={selectedStudent.institution || ''} onChange={handleInputChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Programa</FormLabel>
                  <Input
                    name="program_finished"
                    value={selectedStudent.program_finished || ''}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Estado</FormLabel>
                  <Select name="status" value={selectedStudent.status || ''} onChange={handleInputChange}>
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                    <option value="Graduado">Graduado</option>
                    <option value="Retirado">Retirado</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Fecha límite de pago</FormLabel>
                  <Input
                    name="datePayment"
                    type="date"
                    value={selectedStudent.datePayment || ''}
                    onChange={handleInputChange}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Valor</FormLabel>
                  <Input name="valor" type="number" value={selectedStudent.valor || ''} onChange={handleInputChange} />
                </FormControl>

                <FormControl>
                  <FormLabel>Semestre</FormLabel>
                  <Input name="semester" value={selectedStudent.semester || ''} onChange={handleInputChange} />
                </FormControl>
              </Stack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting} loadingText="Guardando">
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Diálogo de confirmación para eliminar */}
      <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={onAlertClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar Estudiante
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Estás seguro? Esta acción no se puede deshacer.
              {studentToDelete && (
                <Text mt={2} fontWeight="bold">
                  Estudiante: {studentToDelete.name}
                </Text>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onAlertClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
